import { BreakpointObserver } from '@angular/cdk/layout';
import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faRocket } from '@fortawesome/free-solid-svg-icons';
import { TranslocoDirective } from '@jsverse/transloco';
import { map } from 'rxjs';
import { isTenantAdmin } from '../../../models/enums/user-role.enum';
import { AuthService } from '../../../services/auth.service';
import { BillingStatusService } from '../../../services/billing-status.service';
import { ONBOARDING_TOTAL, OnboardingDataService } from '../../../services/onboarding-data.service';
import { WineryContextService } from '../../../services/winery-context.service';
import { ScreenSizeService } from '../../../services/screen-size.service';
import { DAILY_ITEMS, NavItem, SET_UP_ITEMS } from '../../shell/nav-items';
import { BadgeComponent } from '../badge.component';
import { TopBarAccountComponent } from './top-bar-account.component';
import { BrandMarkComponent } from '../brand-mark/brand-mark.component';

// Same width as `--breakpoint-nav` in theme.css (`nav:` / `max-nav:`); a media query cannot read the CSS variable.
const NAV_ON_ONE_ROW_QUERY = '(min-width: 77.5rem)';

/** Top bar: brand, booking-page shortcut and account menu; the section links only on the computer (the tablet has the tab bar). */
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-top-bar',
  standalone: true,
  imports: [NgTemplateOutlet, RouterLink, RouterLinkActive, FaIconComponent, TranslocoDirective, BrandMarkComponent, TopBarAccountComponent, BadgeComponent],
  templateUrl: './top-bar.component.html',
})
export class TopBarComponent {
  private readonly _auth = inject(AuthService);
  private readonly _profile = toSignal(this._auth.currentProfile$);
  private readonly _breakpoints = inject(BreakpointObserver);
  private readonly _wineryContext = inject(WineryContextService);
  private readonly _billing = inject(BillingStatusService);

  public readonly size = inject(ScreenSizeService).size;

  /** Below the nav breakpoint the links wrap under the brand row, so the account area has to come first in the tab order too. */
  public readonly navOnOneRow = toSignal(this._breakpoints.observe(NAV_ON_ONE_ROW_QUERY).pipe(map((state) => state.matches)), {
    initialValue: this._breakpoints.isMatched(NAV_ON_ONE_ROW_QUERY),
  });

  public readonly gettingStartedIcon = faRocket;
  public readonly onboarding = inject(OnboardingDataService);
  public readonly onboardingTotal = ONBOARDING_TOTAL;

  /** Links, plan chip and account paint together, once everything that decides them has answered; a failed profile read must not leave the bar bare. */
  public readonly ready = computed(() => {
    if (this._auth.profileFailed()) return true;
    const profile = this._profile();
    const winery = this._wineryContext.state();
    if (!profile || (winery !== 'ready' && winery !== 'error')) return false;
    return !isTenantAdmin(profile.role) || (this._billing.settled() && (this.onboarding.loaded() || this.onboarding.failed()));
  });

  /** Without a profile the role is unknown, so the links would be the staff ones for an owner; the account menu alone stays. */
  public readonly showLinks = computed(() => this.ready() && this._profile() != null);

  public readonly showGettingStarted = computed(() => isTenantAdmin(this._profile()?.role) && this.onboarding.openCount() > 0);
  public readonly dailyItems = DAILY_ITEMS;

  public readonly setUpItems = computed<readonly NavItem[]>(() => {
    const isOwner = isTenantAdmin(this._profile()?.role);
    return SET_UP_ITEMS.map((item) => ({ ...item, route: (!isOwner && item.staffRoute) || item.route }));
  });
}
